import React, { useState, useEffect, useContext, useRef } from 'react';
import { UniverseBuilder } from '@baemingo/report-events';
import { Item, PaymentMethod } from '@baemingo/report-events/dist/types';
import { useSelector, useDispatch, useLogger } from '~Hooks';
import { v4 as uuid } from 'uuid';

import * as actions from '~actions';
import { useStorage } from '~Components/Storage';

/**
 * Create a context with an empty universe. This context acts as a
 * state that we can access from child components. We use this in app.tsx
 */
const Context = React.createContext(new UniverseBuilder().serialize());

export const ReportsUniverseProvider: React.FunctionComponent = ({ children }) => {
  // A local universe
  const universe = useRef(new UniverseBuilder());

  // The current index of which event we've added to the universe from redux
  const index = useRef(0);

  // Get events from state. Events are added via UI actions or synced from backend
  const events = useSelector((state) => state.reportEvents);

  // Get snapshot from state. If this changes we override our current universe
  const snapshot = useSelector((state) => state.reportSnapshot.snapshot);

  // We save the serialized universe in state to trigger re-renders
  const [state, setState] = useState(universe.current.serialize());

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: ReportsUniverseProvider');
    return () => {
      mountLogger.info('Unmounted: ReportsUniverseProvider');
    };
  }, []);

  useEffect(() => {
    try {
      // Start by removing any events we've already added, then add the event
      events.slice(index.current).forEach((event) => universe.current.addEvent(event));

      // Update our active index
      index.current = events.length;

      // Save to state and trigger a re-render
      setState(universe.current.serialize());
    } catch (e) {
      console.error(e);
    }
  }, [events]);

  useEffect(() => {
    if (!snapshot) {
      return;
    }

    universe.current = new UniverseBuilder(snapshot);

    setState(universe.current.serialize());
  }, [snapshot]);

  return <Context.Provider value={state}>{children}</Context.Provider>;
};

export const useReportsUniverse = () => {
  return useContext(Context);
};

export const useReportsApi = () => {
  const [, , venueId] = location.hash.split('/');

  const dispatch = useDispatch();
  const [storage] = useStorage();

  const generateEventMeta = () => ({
    createdAt: new Date().toISOString(),
    eventId: uuid(),
    sentAt: null,
    deviceId: storage.taxComplianceDeviceName || 'Unknown',
    venueId,
  });

  const API = {
    addOrder: (
      id: string,
      items: Item[],
      paymentMethods: PaymentMethod[],
      tip = 0,
      staffId?: string | null,
    ) => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'ADD_ORDER',
          items,
          tip,
          id,
          paymentMethods,
          staffId,
        }),
      );
    },

    refundOrder: (
      id: string,
      items: Item[],
      paymentMethods: PaymentMethod[],
      staffId?: string,
    ) => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'REFUND_ORDER',
          items,
          id,
          paymentMethods,
          staffId,
        }),
      );
    },
    addExpense: (amount: number, paymentMethod: string) => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'ADD_EXPENSE',
          amount,
          paymentMethod,
        }),
      );
    },
    closeDay: () => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'CLOSE_DAY',
        }),
      );
    },
    startDay: () => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'START_DAY',
        }),
      );
    },
    receiptCopyPrinted: (amount: number) => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'RECEIPT_COPY_PRINTED',
          amount,
        }),
      );
    },
    receiptCopyEmailed: (amount: number) => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'RECEIPT_COPY_EMAILED',
          amount,
        }),
      );
    },
    cashBoxOpened: () => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'CASH_BOX_OPENED',
        }),
      );
    },
    refundReceiptPrinted: (amount: number) => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'REFUND_RECEIPT_PRINTED',
          amount,
        }),
      );
    },
    provisionalReceiptPrinted: (amount: number) => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'PROVISIONAL_RECEIPT_PRINTED',
          amount,
        }),
      );
    },
    orderLineCorrected: (typeOfCorrection: 'REMOVE_ITEM' | 'REMOVE_CHEQUE', amount: number) => {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'ORDER_LINE_CORRECTED',
          typeOfCorrection,
          amount,
        }),
      );
    },
    tipCorrection(change: number, paymentMethod: string, orderId: string) {
      dispatch(
        actions.addReportsEvent({
          ...generateEventMeta(),
          type: 'TIP_CORRECTION',
          change,
          paymentMethod,
          id: orderId,
        }),
      );
    },
  };

  return API;
};
